import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  AcceleratorInstallationDTO,
  DialerNumberOption,
  TwilioConnection,
} from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";

interface Props {
  open: boolean;
  /** Active connections the user can deploy into. */
  connections: TwilioConnection[];
  onClose: () => void;
  /** Called with the freshly-created (pending) installations. */
  onInstalled: (dtos: AcceleratorInstallationDTO[]) => void;
}

type NumbersState =
  | { status: "loading" }
  | { status: "ready"; numbers: DialerNumberOption[] }
  | { status: "error"; message: string };

type Step = 1 | 2;

/**
 * Two-step install wizard for the Dialer accelerator.
 *   Step 1 — choose the Twilio account.
 *   Step 2 — select one or more voice numbers; we deploy a dialer on each.
 * Numbers that already have a dialer are shown but not selectable. Run the
 * wizard again to deploy into another account.
 */
export function InstallDialerDialog({ open, connections, onClose, onInstalled }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [connectionId, setConnectionId] = useState("");
  const [numbers, setNumbers] = useState<NumbersState>({ status: "loading" });
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    setStep(1);
    setError(null);
    setSearch("");
    setSelected([]);
    setConnectionId((prev) => prev || connections[0]?.id || "");
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus();
    };
  }, [open, connections, onClose]);

  // Fetch numbers lazily when entering step 2 (refetches if the account changed).
  useEffect(() => {
    if (!open || step !== 2 || !connectionId) return;
    let cancelled = false;
    setNumbers({ status: "loading" });
    setSelected([]);
    setSearch("");
    void api.accelerators
      .dialerNumbers(connectionId)
      .then((res) => {
        if (!cancelled) setNumbers({ status: "ready", numbers: res.numbers });
      })
      .catch((err) => {
        if (cancelled) return;
        setNumbers({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load numbers",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, connectionId]);

  const filtered = useMemo(() => {
    if (numbers.status !== "ready") return [];
    const term = search.trim().toLowerCase();
    if (!term) return numbers.numbers;
    return numbers.numbers.filter(
      (n) =>
        n.phoneNumber.toLowerCase().includes(term) ||
        (n.friendlyName ?? "").toLowerCase().includes(term),
    );
  }, [numbers, search]);

  const installableFiltered = useMemo(
    () => filtered.filter((n) => !n.alreadyInstalled),
    [filtered],
  );
  const allSelected =
    installableFiltered.length > 0 && installableFiltered.every((n) => selected.includes(n.sid));

  if (!open) return null;

  function toggle(sid: string) {
    setSelected((prev) => (prev.includes(sid) ? prev.filter((s) => s !== sid) : [...prev, sid]));
  }
  function toggleAll() {
    const sids = installableFiltered.map((n) => n.sid);
    setSelected((prev) =>
      allSelected ? prev.filter((s) => !sids.includes(s)) : [...new Set([...prev, ...sids])],
    );
  }

  async function submit() {
    if (selected.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.accelerators.installDialer({
        connectionId,
        phoneNumberSids: selected,
      });
      onInstalled(res.installations);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Install failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const noConnections = connections.length === 0;
  const selectedConn = connections.find((c) => c.id === connectionId) ?? null;
  const hasNumbers = numbers.status === "ready" && numbers.numbers.length > 0;
  const noNumbers = numbers.status === "ready" && numbers.numbers.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-dialer-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm dark:bg-zinc-950/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card dark:ring-1 dark:ring-inset dark:ring-white/[0.03]">
        {/* Header + stepper */}
        <div className="border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <h2
            id="install-dialer-title"
            className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50"
          >
            Install Dialer
          </h2>
          {!noConnections && (
            <Stepper
              step={step}
              labels={["Account", "Numbers"]}
              onJump={(s) => {
                if (s < step) setStep(s);
              }}
            />
          )}
        </div>

        {noConnections ? (
          <div className="px-5 py-6">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-[13px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              Connect an active Twilio account before installing.
            </div>
          </div>
        ) : step === 1 ? (
          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            <p className="mb-2 text-[13px] text-zinc-600 dark:text-zinc-400">
              Which connected Twilio account should the dialers deploy into?
            </p>
            <div
              role="radiogroup"
              aria-label="Twilio accounts"
              className="min-h-0 flex-1 overflow-y-auto rounded-md border border-zinc-200 dark:border-zinc-800"
            >
              {connections.map((c) => {
                const isSel = c.id === connectionId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    onClick={() => setConnectionId(c.id)}
                    onDoubleClick={() => setStep(2)}
                    className={
                      "flex w-full items-center gap-3 border-b border-zinc-100 px-3 py-2.5 text-left transition last:border-b-0 dark:border-zinc-800/60 " +
                      (isSel
                        ? "bg-brand-50 dark:bg-brand-500/10"
                        : "hover:bg-zinc-50 dark:hover:bg-zinc-800/40")
                    }
                  >
                    <Radio selected={isSel} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">
                        {c.friendlyName ?? c.accountSid}
                      </span>
                      <span className="block truncate font-mono text-[11.5px] text-zinc-500">
                        {c.accountSid}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <Link
              to="/connections/new"
              onClick={onClose}
              className="mt-3 inline-flex items-center gap-1 text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
            >
              Connect another Twilio account →
            </Link>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            <p className="mb-2 text-[13px] text-zinc-600 dark:text-zinc-400">
              Select the voice numbers on{" "}
              <span className="font-medium text-zinc-700 dark:text-zinc-300">
                {selectedConn?.friendlyName ?? selectedConn?.accountSid}
              </span>{" "}
              to deploy a dialer on each.
            </p>

            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[12px] font-medium text-zinc-700 dark:text-zinc-300">
                Voice numbers
              </span>
              {hasNumbers && (
                <div className="flex items-center gap-3 text-[11.5px]">
                  <span className="text-zinc-500">{selected.length} selected</span>
                  {installableFiltered.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleAll}
                      disabled={submitting}
                      className="text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                    >
                      {allSelected ? "Clear" : "Select all"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {hasNumbers && (
              <input
                type="search"
                value={search}
                disabled={submitting}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by number or name"
                aria-label="Search voice numbers"
                className="mb-2 h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70"
              />
            )}

            {hasNumbers && installableFiltered.length === 0 && !search && (
              <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                Every voice number on this account already has a dialer.{" "}
                <Link
                  to="/connections/new"
                  onClick={onClose}
                  className="font-medium underline underline-offset-2"
                >
                  Connect another account
                </Link>{" "}
                to deploy more.
              </div>
            )}

            <div
              role="group"
              aria-label="Voice numbers"
              className="min-h-0 flex-1 overflow-y-auto rounded-md border border-zinc-200 dark:border-zinc-800"
            >
              {numbers.status === "loading" && <RowSkeleton />}

              {numbers.status === "error" && (
                <p className="px-3 py-6 text-center text-[13px] text-red-600 dark:text-red-400">
                  {numbers.message}
                </p>
              )}

              {noNumbers && (
                <p className="px-3 py-6 text-center text-[13px] text-zinc-500 dark:text-zinc-500">
                  No voice-capable numbers synced yet. They appear shortly after a sync.
                </p>
              )}

              {hasNumbers && filtered.length === 0 && (
                <div className="px-3 py-6 text-center">
                  <p className="text-[13px] text-zinc-600 dark:text-zinc-400">
                    No numbers match &ldquo;{search}&rdquo;.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="mt-1.5 text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {filtered.map((n) => {
                const checked = selected.includes(n.sid);
                const installed = n.alreadyInstalled;
                return (
                  <button
                    key={n.sid}
                    type="button"
                    role="checkbox"
                    aria-checked={installed ? true : checked}
                    aria-disabled={installed || undefined}
                    disabled={submitting || installed}
                    onClick={() => !installed && toggle(n.sid)}
                    className={
                      "flex w-full items-center gap-3 border-b border-zinc-100 px-3 py-2.5 text-left transition last:border-b-0 dark:border-zinc-800/60 " +
                      (installed
                        ? "cursor-default opacity-60"
                        : checked
                          ? "bg-brand-50 dark:bg-brand-500/10"
                          : "hover:bg-zinc-50 dark:hover:bg-zinc-800/40")
                    }
                  >
                    <Checkbox checked={installed ? true : checked} muted={installed} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-[13.5px] text-zinc-900 dark:text-zinc-100">
                        {n.phoneNumber}
                      </span>
                      {n.friendlyName && (
                        <span className="block truncate text-[11.5px] text-zinc-500">
                          {n.friendlyName}
                        </span>
                      )}
                    </span>
                    <span className="flex flex-none items-center gap-1">
                      {installed ? (
                        <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500 ring-1 ring-inset ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-500 dark:ring-zinc-700">
                          Installed
                        </span>
                      ) : (
                        <>
                          <CapBadge label="Voice" />
                          {n.capabilities.sms && <CapBadge label="SMS" />}
                          {n.capabilities.mms && <CapBadge label="MMS" />}
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {error && (
              <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </p>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-zinc-200 px-5 py-4 dark:border-zinc-800">
          {step === 2 && !noConnections && (
            <p className="mb-3 text-[12px] text-zinc-500 dark:text-zinc-500">
              Creates a TwiML App per number and points each at it. Reversible.
            </p>
          )}
          <div className="flex justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => (step === 2 ? setStep(1) : onClose())}
              disabled={submitting}
            >
              {step === 2 ? "Back" : "Cancel"}
            </Button>
            {step === 1 ? (
              <Button onClick={() => setStep(2)} disabled={noConnections || !connectionId}>
                Next
              </Button>
            ) : (
              <Button onClick={() => void submit()} disabled={submitting || selected.length === 0}>
                {submitting
                  ? "Installing…"
                  : `Install ${selected.length || ""} ${selected.length === 1 ? "dialer" : "dialers"}`.trim()}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Stepper({
  step,
  labels,
  onJump,
}: {
  step: Step;
  labels: [string, string];
  onJump: (s: Step) => void;
}) {
  return (
    <ol className="mt-3 flex items-center gap-2 text-[12px]">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        const active = n === step;
        const done = n < step;
        return (
          <li key={label} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onJump(n)}
              disabled={n >= step}
              className={
                "flex items-center gap-1.5 rounded-full px-1 transition " +
                (n < step ? "cursor-pointer" : "cursor-default")
              }
            >
              <span
                className={
                  "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ring-1 ring-inset " +
                  (active
                    ? "bg-brand-600 text-white ring-brand-600 dark:bg-brand-500 dark:ring-brand-500"
                    : done
                      ? "bg-brand-50 text-brand-700 ring-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
                      : "bg-zinc-100 text-zinc-500 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-500 dark:ring-zinc-700")
                }
              >
                {done ? "✓" : n}
              </span>
              <span
                className={
                  active
                    ? "font-medium text-zinc-900 dark:text-zinc-100"
                    : "text-zinc-500 dark:text-zinc-500"
                }
              >
                {label}
              </span>
            </button>
            {i === 0 && (
              <span className="h-px w-6 bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Radio({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={
        "flex h-4 w-4 flex-none items-center justify-center rounded-full border " +
        (selected
          ? "border-brand-600 dark:border-brand-400"
          : "border-zinc-300 dark:border-zinc-600")
      }
    >
      {selected && <span className="h-2 w-2 rounded-full bg-brand-600 dark:bg-brand-400" />}
    </span>
  );
}

function Checkbox({ checked, muted }: { checked: boolean; muted?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={
        "flex h-4 w-4 flex-none items-center justify-center rounded border transition " +
        (checked
          ? muted
            ? "border-zinc-300 bg-zinc-300 text-white dark:border-zinc-600 dark:bg-zinc-600"
            : "border-brand-600 bg-brand-600 text-white dark:border-brand-500 dark:bg-brand-500"
          : "border-zinc-300 dark:border-zinc-600")
      }
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" aria-hidden="true">
          <path
            d="M2.5 6.5 5 9l4.5-5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

function CapBadge({ label }: { label: string }) {
  return (
    <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
      {label}
    </span>
  );
}

function RowSkeleton() {
  return (
    <div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-zinc-100 px-3 py-2.5 last:border-b-0 dark:border-zinc-800/60"
        >
          <div className="h-4 w-4 flex-none animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="h-2 w-20 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
        </div>
      ))}
    </div>
  );
}
